import React, { useState, useRef, useEffect } from 'react';
import { Button } from './Button';
import { Modal } from './Modal';

interface DropdownAction {
  label: string;
  onClick?: () => void;
  icon?: React.ReactNode;
  subActions?: DropdownAction[];
}

interface DropdownMenuProps {
  label: string;
  actions: DropdownAction[];
  buttonClassName?: string;
  menuClassName?: string;
  menuPlacement?: 'right' | 'left';
}

export const DropdownMenu: React.FC<DropdownMenuProps> = ({
  label,
  actions,
  buttonClassName = '',
  menuClassName = '',
  menuPlacement = 'right',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState<DropdownAction | null>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const handleActionClick = (action: DropdownAction) => {
    if (action.subActions) {
      setSelectedAction(action);
    } else if (action.onClick) {
      action.onClick();
      setIsOpen(false);
    }
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <div className="relative inline-block text-left" ref={dropdownRef}>
      <Button
        preset="white"
        size="md"
        className={`inline-flex justify-center items-center ${buttonClassName}`}
        onPress={toggleDropdown}
      >
        {label}
        <svg
          className="-mr-1 ml-2 h-5 w-5"
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          aria-hidden="true"
        >
          <path
            fillRule="evenodd"
            d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
            clipRule="evenodd"
          />
        </svg>
      </Button>

      {isOpen && (
        <div
          className={`origin-top-${menuPlacement} absolute ${menuPlacement === 'right' ? 'right-0' : 'left-0'} mt-2 w-56 rounded-md shadow-lg bg-white ring-1 ring-black/30 focus:outline-none z-10 ${menuClassName}`}
          style={{ zIndex: 100 }}
        >
          <div className="py-1" role="menu" aria-orientation="vertical">
            {actions.map((action, index) => (
              <button
                key={index}
                className="w-full text-base text-left px-4 py-2 text-md cursor-pointer text-gray-700 hover:bg-gray-100 hover:text-gray-900 flex items-center justify-between"
                role="menuitem"
                onClick={() => handleActionClick(action)}
              >
                <div className="flex items-center">
                  {action.icon && <span className="mr-2">{action.icon}</span>}
                  {action.label}
                </div>
                {action.subActions && (
                  <svg
                    className="h-4 w-4"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                )}
              </button>
            ))}
          </div>
        </div>
      )}

      {selectedAction && (
        <Modal
          title={selectedAction?.label}
          onClose={() => setSelectedAction(null)}
          width="small"
          noContentPadding={true}
          actions={[
            {
              label: 'Close',
              preset: 'white',
              onPress: () => setSelectedAction(null),
            },
          ]}
        >
          <div>
            {selectedAction?.subActions?.map((subAction, index, array) => (
              <button
                key={index}
                className={`text-lg cursor-pointer w-full px-4 py-3 text-left text-gray-700 hover:bg-gray-50 flex items-center ${
                  index !== array.length - 1 ? 'border-b border-gray-200' : ''
                }`}
                onClick={() => {
                  if (subAction.onClick) {
                    subAction.onClick();
                    setSelectedAction(null);
                    setIsOpen(false);
                  }
                }}
              >
                {subAction.icon && <span className="mr-2">{subAction.icon}</span>}
                {subAction.label}
              </button>
            ))}
          </div>
        </Modal>
      )}
    </div>
  );
}; 